<script lang="ts" setup>

import type {MultiChoice, Option} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {computed} from 'vue';
import LazyImage from '@/vue/atoms/lazy-image.vue';

const props = defineProps<{
  question: MultiChoice;
  answers: UuidDTO[];
  customAnswer?: string;
}>();

const selectedOptions = computed<Option[]>(() =>
  props.answers
    .map((uuid) => props.question.options.find(opt => opt.id === uuid))
    .filter((opt): opt is Option => Boolean(opt)));

const displayTexts = computed(() =>
  selectedOptions.value.map((option) =>
    props.customAnswer ? `${option.text}, ${props.customAnswer}` : option.text));
</script>

<template>
  <div class="multichoice-answers">    
    <template v-for="(option, index) in selectedOptions" :key="option.id">
      <div
        v-if="option.image && option.image !== null"
        class="image-option-container"
      >
        <lazy-image
          :src="option.image"
          :alt-txt="option.text"
          :width="384"
          class="option-image"
        />

        <div class="text-body-bold option-text">
          {{ displayTexts[index] }}
        </div>
      </div>
      <div
        v-else
        class="text-option-container"
      >
        {{ displayTexts[index] }}
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
.multichoice-answers {
  display: grid;
  grid-template-columns: repeat(auto-fill, 205px);
  gap: var(--Spacing-spacing-3);
  justify-content: start;

  @include screen-md {
    grid-template-columns: repeat(auto-fill, 220px);
  }

  @include screen-lg {
    grid-template-columns: repeat(auto-fill,320px);
  }
}

.image-option-container {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: var(--Spacing-spacing-5);
  border-radius: var(--border-radius);
  color: var(--Neutral-UI-neutral-ui-100);
  background: var(--Neutral-UI-neutral-ui-20);
  min-height: 104px;

  @include screen-md {
    min-height: 130px;
  }

  @include screen-lg {
    min-height: 166px;
  }
}

.option-image {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.option-text {
  text-align: center;
  padding-top: var(--Spacing-spacing-3);
}
</style>
